import { createSignal } from "solid-js";

import type { Client } from "stoat.js";

import { PRESET_IDS, PresetId } from "./presets";

/**
 * Look a server owner can set for everyone inside their server
 * (stored as JSON in the server's `theme` field)
 */
export type ServerTheme = {
  preset?: PresetId;
  accent?: string;
};

const RE_HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

/**
 * Parse and validate a server theme
 * @param json Raw JSON from the server
 * @returns Theme, or nothing when missing or invalid
 */
export function parseServerTheme(
  json?: string | null,
): ServerTheme | undefined {
  if (!json) return undefined;

  try {
    const data = JSON.parse(json);
    const theme: ServerTheme = {};

    if (PRESET_IDS.includes(data?.preset)) theme.preset = data.preset;
    if (typeof data?.accent === "string" && RE_HEX_COLOUR.test(data.accent)) {
      theme.accent = data.accent;
    }

    return theme.preset || theme.accent ? theme : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Fetch a server's theme (the SDK does not keep this TYPUS field)
 * @param client Client
 * @param serverId Server id
 * @returns Theme, or null if the server has none
 */
export async function fetchServerTheme(
  client: Client,
  serverId: string,
): Promise<ServerTheme | null> {
  const server = (await client.api.get(
    `/servers/${serverId}` as never,
  )) as unknown as { theme?: string | null };

  return parseServerTheme(server.theme) ?? null;
}

const [activeServerTheme, setActiveServerTheme] = createSignal<ServerTheme>();

/**
 * Theme of the server currently being viewed, if any
 */
export { activeServerTheme, setActiveServerTheme };
